import { Link, Hero, theme } from '@kvdbil/components';
import { Color, ColorShade, TextColor } from '@kvdbil/components/types/Types';
import React, { memo, ReactNode } from 'react';
import styled from 'styled-components';
import { DarkOrLight } from '~/App/shared/interfaces/Theme';
import { OptionalHeroRecord } from '~/config/generated/graphql';
import { CmsColors } from '../views/Start/components/CampaignBanners/SmallPromoBanner';
import HeroImage from '~/App/components/DatoCMS/HeroImage';
import { resolveTrustedPathOrUrl } from '~/helpers/router';
import { localizationSelector } from '../shared/selectors/localization';
import { useSelector } from 'react-redux';

const Wrapper = styled.div<Pick<CmsColors, 'textColor'>>`
  span {
    color: ${({ textColor }) => theme.colors.text[textColor]};
  }
`;

type Props = {
  data: OptionalHeroRecord;
  dynamicGutter?: {
    tabletVw: number;
    laptopVw: number;
  };
  contentMaxWidth?: number;
  isLoading?: boolean;
  children?: ReactNode;
};

function OptionalHero({
  data,
  dynamicGutter,
  contentMaxWidth,
  isLoading,
  children
}: Props) {
  const button = data?.button?.[0];
  const { domainLocale } = useSelector(localizationSelector);
  const { path, url } = resolveTrustedPathOrUrl(button?.link, domainLocale);

  if (!data?.showHero) {
    return null;
  }

  return (
    <Wrapper textColor={button?.textColor as TextColor}>
      <Hero
        imageElement={
          data?.image?.responsiveImage && (
            <HeroImage
              responsiveImage={data?.image?.responsiveImage}
              focalPoint={data?.image?.focalPoint}
              tags={data?.image?.tags}
            />
          )
        }
        heading={data.title}
        subheading={data.subtitle}
        headingColor={data.textColor as DarkOrLight}
        button={
          (path || url) && button?.text ? (
            <Link
              isButton
              buttonColor={button?.backgroundColor as Color}
              buttonColorShade={button?.backgroundColorShade as ColorShade}
              buttonSize="large"
              text={button?.text}
              path={path}
              url={url}
            />
          ) : undefined
        }
        contentCentered={data.contentAlignment === 'center'}
        dynamicGutter={dynamicGutter}
        elementShadow
        contentMaxWidth={contentMaxWidth}
        isLoading={isLoading}
        childrenNoWrapper
      >
        {children}
      </Hero>
    </Wrapper>
  );
}

export default memo(OptionalHero);
